import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { TOP_BAR_HEIGHT } from "@/components/layout/components/constants";
import { mdsPanelModeBreakpoints } from "@/design-system/foundations";
import { PanelMode } from "@/design-system/foundations";
import { Button } from "react-aria-components";
import { TopBarDesktopSection } from "@/components/layout/components/top-bar/top-bar-desktop-section";
import { MdsIcon } from "@/design-system/components/icon";
import { MdsIconKind } from "@/design-system/components/icon";
import { MDSButton, MDSButtonVariant, MDSIconButton, MDSText } from "@/mds";
import { MDSTextOverflow } from "@/mds";
import { LogoActive } from "@/assets/icons/logo/logo-active";
import { LogoInactive } from "@/assets/icons/logo/logo-inactive";
import { WordmarkActive } from "@/assets/icons/logo/wordmark-active";
import { SidebarMobile } from "@/components/layout/components/sidebar/sidebar-mobile";
import { useState } from "react";
import { TopBarProfileImage } from "@/components/layout/components/top-bar/top-bar-profile-image";
import { EventContext } from "@/domains/metrics/context";
import { AudioCaptureIndicator } from "@/components/layout/components/AudioCaptureIndicator";
import { BackForwardButtons } from "../back-forward-buttons";
import { clientEnvModule } from "@/modules/client-env";
import { SidebarVariant } from "../sidebar/constants";
import { isMac } from "@/domains/platform/isMac";
import { StoplightSpacer } from "@/components/layout/components/sidebar/sidebar-desktop-section";

interface TopBarProps {
  setIsImportModalOpen: (b: boolean) => void;
  currentSidebarVariant: SidebarVariant;
}

export const TopBar = observer(function TopBar({
  setIsImportModalOpen,
  currentSidebarVariant,
}: TopBarProps) {
  const { store, pageStore } = useAppStore();
  const isDesktop = clientEnvModule.isDesktop();
  const isMacOS = isMac();

  const [isPopUpBarOpen, setIsPopUpBarOpen] = useState(false);
  const togglePopUpBar = () => setIsPopUpBarOpen(prev => !prev);

  const { sidePanelWidth, isSidePanelOpen } = store.sidePanel;
  const isNarrowView = !!store.interface.matchesPanelModeBreakpoint.get(PanelMode.Narrow);
  const isWideView =
    !!store.interface.matchesPanelModeBreakpoint.get(PanelMode.WideSinglePanel) ||
    !!store.interface.matchesPanelModeBreakpoint.get(PanelMode.WideSplitView);

  const handleSearchClick = () => {
    pageStore.quickSearchModal.toggleIsOpen();
    setIsPopUpBarOpen(false);
  };

  const handleNewNoteClick = () => {
    store.notes.composeNewNote({ eventContext: EventContext.GlobalActions });
    setIsPopUpBarOpen(false);
  };

  const handleToggleSidepanel = () => {
    store.sidePanel.toggle();
    setIsPopUpBarOpen(false);
  };

  return (
    <>
      {isNarrowView && isPopUpBarOpen && (
        <SidebarMobile
          onClick={() => setIsPopUpBarOpen(false)}
          setIsImportModalOpen={setIsImportModalOpen}
        />
      )}
      <Wrapper>
        <LeftSection $isNarrow={isNarrowView}>
          {isDesktop && isMacOS && isNarrowView && <StoplightSpacer />}
          {isDesktop && currentSidebarVariant !== SidebarVariant.Expanded && (
            <BackForwardButtons
              navigateBack={store.navigation.goBack}
              navigateForward={store.navigation.goForward}
              canNavigateBack={store.navigation.canGoBack}
              canNavigateForward={store.navigation.canGoForward}
            />
          )}
          {/* Mobile-specific controls */}
          {isNarrowView && (
            <NarrowLeftButtons>
              <TopBarProfileImage togglePopUpBar={togglePopUpBar} />
              <MDSIconButton
                iconKind={MdsIconKind.ComposeInline}
                isDisabled={store.notes.newNoteIsBeingCreated}
                onClick={handleNewNoteClick}
                data-testid="compose-note-button"
              />
            </NarrowLeftButtons>
          )}
          <AudioCaptureIndicator expanded={!isNarrowView} onClickElse={handleToggleSidepanel} />
          <SearchButton onClick={handleSearchClick}>
            <MdsIcon kind={MdsIconKind.Search} />
            <MDSText numberOfLines={1} overflow={MDSTextOverflow.Ellipsis}>
              {isNarrowView ? "Search" : "Search or ask your Mem"}
            </MDSText>
          </SearchButton>
        </LeftSection>
        <RightSection style={{ width: isWideView ? sidePanelWidth : "fit-content" }}>
          <StyledLogoButton
            variant={MDSButtonVariant.Transparent}
            onClick={handleToggleSidepanel}
            $isSidePanelOpen={isSidePanelOpen}
            $isNarrowView={isNarrowView}
          >
            <StyledLogo $isSidePanelOpen={isSidePanelOpen}>
              {!isSidePanelOpen ? (
                <LogoInactive />
              ) : isNarrowView ? (
                <LogoActive />
              ) : (
                <WordmarkActive />
              )}
            </StyledLogo>
          </StyledLogoButton>
          <TopBarDesktopSection />
        </RightSection>
      </Wrapper>
    </>
  );
});

const Wrapper = styled.div(({ theme }) => ({
  width: "100%",
  paddingRight: theme.spacing.sm,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: `${TOP_BAR_HEIGHT}px`,
  WebkitAppRegion: "drag", // Makes the top bar draggable in desktop app
  overflow: "hidden",
  paddingBlock: theme.spacing["2"],
  flexShrink: 0,

  // Responsive styling for narrow view
  [`@media ${mdsPanelModeBreakpoints[PanelMode.Narrow]}`]: {
    flexWrap: "nowrap",
    gap: 0,
  },
}));

const SearchButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  height: 100%;
  flex-grow: 1;
  border-radius: ${({ theme }) => theme.borderRadius.max};
  padding: 0 12px;
  -webkit-app-region: no-drag;
  min-width: 0px;

  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: ${({ theme }) => theme.lineHeights.small};
  color: ${({ theme }) => theme.colors.text.primary};

  background-color: ${({ theme }) => theme.colors.background.button.chrome.default};
  transition: background-color 0.2s ease-in-out, width 0.2s ease-in-out;
  
  &[data-hovered] {
    background-color: ${({ theme }) => theme.colors.background.button.chrome.hover};
  }

  // Responsive styling for narrow view
  @media ${mdsPanelModeBreakpoints[PanelMode.Narrow]} {
    max-width: none;
    flex-grow: 1;
    margin: 0 8px;
  },
`;

const StyledLogoButton = styled(MDSButton)<{ $isSidePanelOpen: boolean; $isNarrowView: boolean }>`
  padding-top: ${({ theme }) => theme.spacing["5"]};
  padding-bottom: ${({ theme }) => theme.spacing["5"]};
  padding-left: ${({ theme, $isNarrowView }) =>
    $isNarrowView ? theme.spacing["1"] : theme.spacing["5"]};
  padding-right: ${({ theme, $isNarrowView }) =>
    $isNarrowView ? theme.spacing["2"] : theme.spacing["5"]};

  color: ${({ theme, $isSidePanelOpen, $isNarrowView }) =>
    $isSidePanelOpen
      ? $isNarrowView
        ? theme.colors.icon.brand
        : theme.colors.text.contrast
      : theme.colors.icon.onChromeInactive};
  &[data-hovered]:not([data-disabled]) {
    background-color: transparent;
  }
`;

const LeftSection = styled.div<{ $isNarrow?: boolean }>`
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  overflow: hidden;
  flex-grow: 1;
  gap: ${({ theme, $isNarrow }) => ($isNarrow ? theme.spacing["0"] : theme.spacing["2"])};
`;

const NarrowLeftButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  /* avoid macOS stoplights */
`;

// Right section container
const RightSection = styled.div`
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  overflow: hidden;
`;

const StyledLogo = styled.div<{ $isSidePanelOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    background: transparent;
  }
`;
